import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, type SortDirection } from 'used-vehicle-contracts';

/** Far beyond any list this workspace will hold, and small enough to stay a sane database offset. */
const MAX_PAGE = 1_000_000;

const boundedInteger = (raw: string | null, fallback: number, max: number): number => {
  const parsed = Number(raw);

  // A hand-edited `?page=-3` or `?pageSize=5000` is a request the service would refuse, so the URL is
  // read as a suggestion rather than as an instruction.
  return Number.isInteger(parsed) && parsed >= 1 ? Math.min(parsed, max) : fallback;
};

export interface ListSearchParams {
  page: number;
  pageSize: number;
  /** The raw value in the URL, `''` when it carries none. */
  value: (key: string) => string;
  /** The URL's `sortBy` when the contract's enum accepts it; the caller's default otherwise. */
  sortBy: <TSortBy extends string>(allowed: readonly TSortBy[], fallback: TSortBy) => TSortBy;
  /** The URL's direction, or the one the list opens on — newest first reads differently per list. */
  sortDirection: (fallback: SortDirection) => SortDirection;
  /** Writes these keys — `''` removes one — and returns to page 1 unless `page` is among them. */
  update: (changes: Record<string, string>) => void;
  setPage: (page: number) => void;
  setSort: (sortBy: string, direction: SortDirection) => void;
}

/**
 * The position of a server-paged list, held in the URL (plan §11 fix 12).
 *
 * Page, page size, order and filters all describe *which* rows the service was asked for, so they
 * belong in the address: a reload, a bookmark or the browser's Back button then land on the list the
 * person was looking at rather than on page 1 of everything. Changing anything but the page starts the
 * list again, because page 4 of one filter says nothing about page 4 of another.
 */
export const useListSearchParams = (): ListSearchParams => {
  const [searchParams, setSearchParams] = useSearchParams();

  const update = useCallback(
    (changes: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);

      for (const [key, value] of Object.entries(changes)) {
        if (value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }

      if (!('page' in changes)) {
        next.delete('page');
      }

      // `replace` keeps a filter typed letter by letter out of the browser's history.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback((page: number) => update({ page: String(page) }), [update]);

  const setSort = useCallback((sortBy: string, direction: SortDirection) => update({ sortBy, sortDirection: direction }), [update]);

  const value = useCallback((key: string): string => searchParams.get(key) ?? '', [searchParams]);

  const sortBy = useCallback(
    <TSortBy extends string>(allowed: readonly TSortBy[], fallback: TSortBy): TSortBy => {
      const raw = searchParams.get('sortBy');

      return allowed.find((option) => option === raw) ?? fallback;
    },
    [searchParams],
  );

  const sortDirection = useCallback(
    (fallback: SortDirection): SortDirection => {
      const raw = searchParams.get('sortDirection');

      return raw === 'asc' || raw === 'desc' ? raw : fallback;
    },
    [searchParams],
  );

  return {
    page: boundedInteger(searchParams.get('page'), 1, MAX_PAGE),
    pageSize: boundedInteger(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE),
    value,
    sortBy,
    sortDirection,
    update,
    setPage,
    setSort,
  };
};
